import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Alert } from '@lightspeed/flame/Alert';

import { getLanguage } from 'helpers/locale';

import { createMASBaseConfig, MAS_CONFIG } from '../shared/config';
import { useLocale } from '../shared/useLocale';
import { useMASScript } from '../shared/useMASScript';

// Extended configuration for MAS UI
interface MerchantAccountSpaceConfig {
  baseUrl: string;
  productCode: string;
  entityType: 'subscription' | 'location';
  entityId: string;
  locale: string;
  settings?: {
    overview?: {
      onChatSupportClick?: () => void;
      onCancelSubscriptionClick?: () => void;
    };
  };
}

// Custom element type
interface MerchantAccountSpaceElement extends HTMLElement {
  init?: (config: MerchantAccountSpaceConfig) => void;
  update?: (config: Partial<MerchantAccountSpaceConfig>) => void;
}

// JSX declaration for the custom element
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'merchant-account-space': React.DetailedHTMLProps<
        React.HTMLAttributes<MerchantAccountSpaceElement>,
        MerchantAccountSpaceElement
      >;
    }
  }
}

const CUSTOM_ELEMENT_NAME = 'merchant-account-space';

const MerchantAccountSpace: React.FC = () => {
  const masRef = useRef<MerchantAccountSpaceElement>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const locale = useLocale();
  const currentLocale = getLanguage(locale);
  const lastCancelSubscriptionTrackTime = useRef<number>(0);

  const initMerchantAccountSpace = useCallback(() => {
    const merchantAccountSpace = masRef.current;
    if (!merchantAccountSpace?.init) return;

    const config: MerchantAccountSpaceConfig = createMASBaseConfig(currentLocale);

    // Add Intercom settings if available
    if (window.lsCloud?.Modules?.hasModule('intercom.support')) {
      config.settings = {
        overview: {
          onChatSupportClick: () => {
            window.Intercom?.('show');
          },
          onCancelSubscriptionClick: () => {
            const currentTime = Date.now();
            const oneMinuteInMs = 60 * 1000;
            if (currentTime - lastCancelSubscriptionTrackTime.current >= oneMinuteInMs) {
              window.Intercom?.('trackEvent', 'cancel_subscription_clicked', {
                productCode: MAS_CONFIG.productCode,
              });
              lastCancelSubscriptionTrackTime.current = currentTime;
            } else {
              window.Intercom?.('show');
            }
          },
        },
      };
    }

    merchantAccountSpace.init(config);
    setIsInitialized(true);
  }, [currentLocale]);

  const { hasError } = useMASScript(CUSTOM_ELEMENT_NAME, initMerchantAccountSpace);

  // Update locale when it changes
  useEffect(() => {
    if (!isInitialized) return;
    masRef.current?.update?.({ locale: currentLocale });
  }, [currentLocale, isInitialized]);

  if (hasError) {
    return (
      <Alert type="danger" m={4} noCloseBtn>
        Something went wrong. Try again.
      </Alert>
    );
  }

  return (
    <div className="notranslate">
      <merchant-account-space ref={masRef} />
    </div>
  );
};

export { MerchantAccountSpace };
